import { Language } from "@/config";
import {
  ApiClient4xxError,
  ApiError,
  ApiErrorResponse,
  ApiPanicError,
  ApiServerError,
  BadGatewayApiServerError,
  BadRequestError,
  ForbiddenError,
  NetworkError,
  NotFoundError,
  UnauthorizedError,
} from "@/@types/api-error";
import * as Sentry from "@sentry/react";

type FetchParameters = Parameters<typeof fetch>;

type FetchUrl = FetchParameters[0];
type FetchOptions = FetchParameters[1];
type FetchApiOptions = Omit<FetchOptions, "method">;

export type ApiClient = ReturnType<typeof clientApi>;
export type GetApiClient = Pick<ApiClient, "get">;
export type PutApiClient = Pick<ApiClient, "put">;
export type PostApiClient = Pick<ApiClient, "post">;
export type DeleteApiClient = Pick<ApiClient, "del">;

export const clientApi = (language: Language) => {
  const headers = () => {
    return {
      "Accept-Language": language,
    };
  };

  function get<T>(url: FetchUrl, options?: FetchApiOptions) {
    return getApi<T>(url, {
      ...options,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      headers: { ...headers(), ...options?.headers },
    });
  }

  function post<T>(url: FetchUrl, options?: FetchApiOptions) {
    return postApi<T>(url, {
      ...options,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      headers: { "Content-Type": "application/json", ...headers(), ...options?.headers },
    });
  }

  function put<T>(url: FetchUrl, options?: FetchApiOptions) {
    return putApi<T>(url, {
      ...options,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      headers: { "Content-Type": "application/json", ...headers(), ...options?.headers },
    });
  }

  function del<T>(url: FetchUrl, options?: FetchApiOptions) {
    return deleteApi<T>(url, {
      ...options,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      headers: { ...headers(), ...options?.headers },
    });
  }

  return {
    del,
    get,
    put,
    post,
  };
};

function getApi<T>(url: FetchUrl, args: FetchApiOptions) {
  return wrapJson<T>(
    fetch(url, {
      method: "GET",
      ...args,
    }),
  );
}

function postApi<T>(url: FetchUrl, args: FetchApiOptions) {
  return wrapJson<T>(
    fetch(url, {
      method: "POST",
      ...args,
    }),
  );
}

function putApi<T>(url: FetchUrl, args: FetchApiOptions) {
  return wrapJson<T>(
    fetch(url, {
      method: "PUT",
      ...args,
    }),
  );
}

function deleteApi<T>(url: FetchUrl, args: FetchApiOptions) {
  return wrapJson<T>(
    fetch(url, {
      method: "DELETE",
      ...args,
    }),
  );
}

export function toApiError(statusCode: number, xTraceId: string, message?: string) {
  if (statusCode >= 500) {
    switch (statusCode) {
      case 502:
        return new BadGatewayApiServerError(xTraceId, message);
      default:
        return new ApiServerError(xTraceId, message);
    }
  }
  if (statusCode >= 400) {
    switch (statusCode) {
      case 400:
        return new BadRequestError(xTraceId, message);
      case 401:
        return new UnauthorizedError(xTraceId, message);
      case 403:
        return new ForbiddenError(xTraceId, message);
      case 404:
        return new NotFoundError(xTraceId, message);
      default:
        return new ApiClient4xxError(statusCode, xTraceId, "api_error_panic");
    }
  }
  return new ApiError(statusCode, xTraceId, "api_error_panic");
}

async function responseToApiError(response: Response) {
  try {
    const errorResponse = (await response.json()) as ApiErrorResponse;
    const statusCode = response.status;
    const xTraceId = response.headers.get("x-trace-id") ?? "";
    const message = errorResponse.message;
    return toApiError(statusCode, xTraceId, message);
  } catch (e) {
    const xTraceId = response.headers.get("x-trace-id") ?? "";
    if (e instanceof Error) {
      return new ApiError(response.status, xTraceId, "api_error_panic", e.message, e);
    }
    return new ApiPanicError(e, xTraceId);
  }
}

//
export async function wrapJson<T>(task: Promise<Response>) {
  try {
    const response = await task;
    if (response.ok) {
      return (await response.json()) as T;
    }
    throw await responseToApiError(response);
  } catch (e) {
    if (e instanceof ApiError) {
      Sentry.captureException(e.cause, {
        tags: {
          xTraceId: e.xTraceId ?? "none",
          statusCode: e.statusCode,
        },
      });
      throw e;
    }
    if (e instanceof Error) {
      const error = new NetworkError(e, "ネットワークエラー");
      Sentry.captureException(error);
      throw error;
    }
    Sentry.captureException(e);
    throw new ApiPanicError(e);
  }
}
